import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import type { MissingTranslationHandlerParams } from '@ngx-translate/core';
import { MissingTranslationHandler, TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { HandleHttpUnauthorizedInterceptor } from '@security/auth';
import { icBrand, icBrandFactory, icWorkingDomain, icWorkingDomainFactory } from '@shared/branding';
import { DateFormats } from '@shared/common';
import { AttachSpinnerToMatchingRequestInterceptor } from '@shared/spinner';
import { supportedConsumptionTypes, supportedConsumptionTypesFactory } from '@user/consumption';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { chartFramePlugin } from './app/plugins/chart-frame.plugin';
import type { IncludeBearerTokenCondition } from 'keycloak-angular';
import {
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  createInterceptorCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { CookieModule } from 'ngx-cookie';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import 'zone.js';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { TranslationLoadingService } from './app/shared/localization';
import { environment } from './environments/environment';

Chart.register(ChartDataLabels, chartFramePlugin);

const tokenCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /^(?!.*\/countries(?:\/.*)?(?:\?.*)?$).+/i,
  bearerPrefix: 'Bearer',
});

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      AngularSvgIconModule.forRoot(),
      NgxSpinnerModule.forRoot(),
      ToastrModule.forRoot(),
      CookieModule.withOptions(),
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoadingService,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useValue: { handle: (params: MissingTranslationHandlerParams) => params.key },
      },
    }),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([includeBearerTokenInterceptor])),
    provideRouter(APP_ROUTES),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HandleHttpUnauthorizedInterceptor,
      multi: true,
    },
    importProvidersFrom(MatDialogModule),
    { provide: icWorkingDomain, useFactory: icWorkingDomainFactory },
    { provide: icBrand, useFactory: icBrandFactory, deps: [icWorkingDomain] },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AttachSpinnerToMatchingRequestInterceptor,
      multi: true,
    },
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [tokenCondition],
    },
    provideKeycloak({
      config: environment.keycloak,
      initOptions: {
        onLoad: 'check-sso',
        checkLoginIframe: false,
        redirectUri: window.location.origin + window.location.pathname,
      },
      // Note: loadUserProfileAtStartUp is handled automatically by the new API if needed,
      // or you can manually fetch it later if your app requires user profile data.
    }),
    provideLuxonDateAdapter({
      parse: {
        dateInput: DateFormats.date,
      },
      display: {
        dateInput: DateFormats.date,
        monthYearLabel: 'LLL yyyy',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'LLLL yyyy',
        monthLabel: '',
      },
    }),
    { provide: supportedConsumptionTypes, useFactory: supportedConsumptionTypesFactory },
    provideCharts(withDefaultRegisterables()),
  ],
}).catch((err) => console.error(err));
